import { getGetMeQueryKey } from '@reelplicate/api-client/users';
import {
  getListWorkspacesQueryKey,
  useDeleteWorkspace,
} from '@reelplicate/api-client/workspaces';
import {
  Button,
  Field,
  Modal,
  ModalClose,
  ModalContent,
  ModalTrigger,
  TextInput,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

/**
 * Delete workspace, behind a dialog that asks for the workspace's name.
 * Afterwards the API has put the owner back at the start of onboarding (when
 * it was their last workspace), so they go there to create a new one.
 */
export function DeleteWorkspaceDialog({ id, name }: { id: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const remove = useDeleteWorkspace();
  const matches = typed.trim() === name;

  const confirm = async () => {
    try {
      await remove.mutateAsync({ id });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListWorkspacesQueryKey() }),
    ]);
    await navigate('/onboarding');
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setTyped('');
      }}
    >
      <ModalTrigger asChild>
        <Button type="button" variant="danger" size="sm">
          Delete workspace
        </Button>
      </ModalTrigger>
      <ModalContent
        size="sm"
        title={`Delete ${name}?`}
        description="This permanently deletes every reel, connected account and all brand data in the workspace. It can’t be undone."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button
              variant="danger"
              disabled={!matches}
              loading={remove.isPending}
              onClick={() => void confirm()}
            >
              Delete workspace
            </Button>
          </>
        }
      >
        <Field label={`Type “${name}” to confirm`}>
          <TextInput
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            placeholder={name}
          />
        </Field>
      </ModalContent>
    </Modal>
  );
}
